"use client";

import React from "react";
import { ArrowRight, Droplets, Flame, PhoneCall, Pipette, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  title: "Drain Cleaning",
  description: "Rapid clearing of backed-up main drains, kitchen sinks, and shower traps using high-pressure hydro-jetting and motorized augers.",
  link: "/services/",
  badge: "Most Requested",
  icon: Droplets,
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_service.png-wvc-sizes",
  alt: "Plumber clearing a clogged residential drain with specialized equipment"
}, {
  title: "Sink & Faucet Repair",
  description: "Complete repair and replacement for leaking faucets, broken garbage disposals, clogged p-traps, and corroded under-sink supply lines.",
  link: "/services/",
  badge: "Same-Day Fix",
  icon: Wrench,
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sink_repair_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sink_repair_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sink_repair_service.png-wvc-sizes",
  alt: "Technician tightening kitchen sink pipe connections and fixtures"
}, {
  title: "Water Heater Repair",
  description: "24/7 diagnostics and fixes for pilot lights, heating elements, leaking tanks, and complete tankless unit replacements.",
  link: "/water-heater/",
  badge: "24/7 Urgent",
  icon: Flame,
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_service.png-wvc-sizes",
  alt: "Emergency plumber inspecting residential hot water heater system"
}, {
  title: "Burst Pipe & Leak Repair",
  description: "Immediate leak isolation, slab leak detection, and frozen pipe restoration across Jonesboro homes and commercial properties.",
  link: "/problems/",
  badge: "Emergency",
  icon: Pipette,
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png-wvc-sizes",
  alt: "Plumber repairing damaged copper water line in a utility room"
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedservices" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs">
            Fast Local Dispatch
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Related Emergency &amp; Routine Services
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Beyond urgent pipe repairs, our Jonesboro crew delivers prompt,
            upfront-priced solutions for everyday plumbing failures and routine
            maintenance.
          </p>
        </div>

        <motion.div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {services.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} data-index={index} variants={itemVariants} className="flex">
                <Card className="group flex h-full flex-col justify-between overflow-hidden border border-border bg-card shadow-sm transition-[box-shadow,transform] duration-200 hover:-translate-y-1 hover:shadow-md">
                  <div>
                    <div className="relative aspect-video w-full overflow-hidden bg-muted">
                      <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      <div className="absolute top-3 left-3">
                        <Badge variant="default" className="bg-primary text-primary-foreground shadow-xs">
                          {service.badge}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="pt-5 pb-2">
                      <div className="mb-2 flex items-center gap-2">
                        <div className="flex size-8 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                          <IconComponent className="size-4" />
                        </div>
                        <CardTitle className="font-default text-lg font-bold text-card-foreground">
                          {service.title}
                        </CardTitle>
                      </div>
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                  </div>

                  <CardContent className="pt-2 pb-5">
                    <CardFooter className="p-0">
                      <Button asChild variant="outline" className="w-full justify-between group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
                        <Link to={service.link}>
                          <span>View Details</span>
                          <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-xl border border-border bg-secondary/50 p-6 sm:flex-row sm:p-8">
          <div>
            <h3 className="font-default text-lg font-semibold text-foreground sm:text-xl">
              Facing an immediate plumbing emergency in Jonesboro?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Our service vans are fully stocked and ready to dispatch right
              now.
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <Button asChild size="lg" className="gap-2">
              <Link to="/contact/">
                <PhoneCall className="size-4" />
                <span>Contact Dispatch</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/services/">Browse All Services</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}